<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useClientStore } from '@/stores/client'
import { type DownloadDriver, downloadDriversApi } from '@/api/entities/review/downloadDrivers'
import { type DriverConfig, driverConfigsApi } from '@/api/entities/review/driverConfigs'

definePage({
  meta: {
    title: '채널 관리',
    action: 'read',
    subject: 'review',
  },
})

const clientStore = useClientStore()
const selectedClient = computed(() => clientStore.selectedClient)

// 상태
const loading = ref(false)
const downloadDrivers = ref<DownloadDriver[]>([])
const driverConfigs = ref<DriverConfig[]>([])
const confirmDialog = ref(false)
const confirmMessage = ref('')
const confirmAction = ref<(() => Promise<void>) | null>(null)

// 스낵바
const snackbar = ref(false)
const snackbarMessage = ref('')
const snackbarColor = ref('success')

// 테이블 헤더
const headers = [
  { title: '사용', key: 'active', sortable: false, width: '80px' },
  { title: '이름', key: 'name', sortable: true },
  { title: '리뷰 예외 시작 날짜', key: 'skip_start_date', sortable: false },
  { title: '리뷰 예외 종료 날짜', key: 'skip_end_date', sortable: false },
  { title: '', key: 'actions', sortable: false, width: '100px' },
]

// 테이블 아이템 계산
interface DriverTableItem {
  download_driver_id: number
  driver_config_id: number | null
  name: string
  active: boolean
  skip_start_date: string | null
  skip_end_date: string | null
}

const tableItems = computed<DriverTableItem[]>(() => {
  return downloadDrivers.value.map(driver => {
    const driverConfig = driverConfigs.value.find(config => config.driver_code === driver.code)

    return {
      download_driver_id: driver.id,
      driver_config_id: driverConfig?.id || null,
      name: driver.name,
      active: !!driverConfig,
      skip_start_date: driverConfig?.skipped_start_date || null,
      skip_end_date: driverConfig?.skipped_end_date || null,
    }
  })
})

// 데이터 로드
const loadDownloadDrivers = async () => {
  try {
    const { success, data } = await downloadDriversApi.listDownloadDrivers({
      driver_state: 'active',
      sort: 'name',
      per_page: 1000,
    })

    if (success && data)
      downloadDrivers.value = data.data || []
  }
  catch (error) {
    console.error('Failed to load download drivers:', error)
  }
}

const loadDriverConfigs = async () => {
  if (!selectedClient.value)
    return

  loading.value = true
  try {
    const { success, data } = await driverConfigsApi.listDriverConfigs(selectedClient.value.id, {
      per_page: 1000,
    })

    if (success && data)
      driverConfigs.value = data.data || []
  }
  catch (error) {
    console.error('Failed to load driver configs:', error)
  }
  finally {
    loading.value = false
  }
}

// 확인 다이얼로그
const showConfirm = (message: string, action: () => Promise<void>) => {
  confirmMessage.value = message
  confirmAction.value = action
  confirmDialog.value = true
}

const executeConfirm = async () => {
  if (confirmAction.value)
    await confirmAction.value()

  confirmDialog.value = false
}

// 알림 표시
const showNotification = (message: string, color: string = 'success') => {
  snackbarMessage.value = message
  snackbarColor.value = color
  snackbar.value = true
}

// 드라이버 설정 저장
const saveDriverConfig = (item: DriverTableItem) => {
  if (item.active) {
    if (item.driver_config_id)
      updateDriverConfig(item)
    else
      createDriverConfig(item)
  }
  else if (item.driver_config_id) {
    deleteDriverConfig(item)
  }
}

const createDriverConfig = (item: DriverTableItem) => {
  showConfirm(
    `${item.name} 채널을 활성화 시키시겠습니까?`,
    async () => {
      if (!selectedClient.value)
        return

      const { success } = await driverConfigsApi.createDriverConfig(selectedClient.value.id, {
        download_driver_id: item.download_driver_id,
        skip_start_date: item.skip_start_date,
        skip_end_date: item.skip_end_date,
      })

      if (success) {
        showNotification(`${item.name} 채널이 활성화 되었습니다.`)
        await loadDriverConfigs()
      }
      else {
        showNotification(`${item.name} 채널을 활성화하지 못했습니다.`, 'error')
      }
    },
  )
}

const updateDriverConfig = (item: DriverTableItem) => {
  showConfirm(
    `${item.name} 채널 정보를 수정하시겠습니까?`,
    async () => {
      if (!selectedClient.value || !item.driver_config_id)
        return

      const { success } = await driverConfigsApi.updateDriverConfig(
        selectedClient.value.id,
        item.driver_config_id,
        {
          skip_start_date: item.skip_start_date,
          skip_end_date: item.skip_end_date,
        },
      )

      if (success) {
        showNotification(`${item.name} 채널 정보가 수정되었습니다.`)
        await loadDriverConfigs()
      }
      else {
        showNotification(`${item.name} 채널 정보를 수정하지 못했습니다.`, 'error')
      }
    },
  )
}

const deleteDriverConfig = (item: DriverTableItem) => {
  showConfirm(
    `${item.name} 채널을 비활성화 시키시겠습니까? 해당 채널의 수집된 상품, 리뷰, 그리고 매핑 데이터가 삭제 됩니다.`,
    async () => {
      if (!selectedClient.value || !item.driver_config_id)
        return

      const { success } = await driverConfigsApi.deleteDriverConfig(
        selectedClient.value.id,
        item.driver_config_id,
      )

      if (success) {
        showNotification(`${item.name} 채널을 비활성화 시켰습니다.`)
        await loadDriverConfigs()
      }
      else {
        showNotification(`${item.name} 채널을 비활성화 시키지 못했습니다.`, 'error')
      }
    },
  )
}

// 감시자
watch(selectedClient, async () => {
  if (selectedClient.value) {
    await loadDownloadDrivers()
    await loadDriverConfigs()
  }
  else {
    downloadDrivers.value = []
    driverConfigs.value = []
  }
})

onMounted(async () => {
  if (selectedClient.value) {
    await loadDownloadDrivers()
    await loadDriverConfigs()
  }
})
</script>

<template>
  <div>
    <!-- 상단 섹션 -->
    <VRow class="mb-4">
      <VCol
        cols="12"
        md="4"
      >
        <ClientSelector />
      </VCol>
      <VCol
        cols="12"
        md="3"
      >
        <VCard>
          <VCardText class="d-flex align-center justify-space-between">
            <div>
              <p class="text-subtitle-2 mb-1">
                사용 채널 수
              </p>
              <h4 class="text-h4">
                {{ driverConfigs.length }}
              </h4>
            </div>
            <VAvatar
              color="info"
              variant="tonal"
              rounded
            >
              <VIcon icon="tabler-server" />
            </VAvatar>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- 채널 목록 -->
    <VCard>
      <VCardTitle>채널 목록</VCardTitle>
      <VCardText>
        <VDataTable
          v-if="tableItems.length > 0"
          :headers="headers"
          :items="tableItems"
          :loading="loading"
          class="elevation-0"
          :row-props="({ item }) => ({ class: item.active ? 'bg-success-lighten' : '' })"
        >
          <!-- 사용 여부 -->
          <template #item.active="{ item }">
            <VSwitch
              v-model="item.active"
              hide-details
              color="success"
            />
          </template>

          <!-- 예외 시작 날짜 -->
          <template #item.skip_start_date="{ item }">
            <VTextField
              v-model="item.skip_start_date"
              type="date"
              density="compact"
              hide-details
              variant="outlined"
            />
          </template>

          <!-- 예외 종료 날짜 -->
          <template #item.skip_end_date="{ item }">
            <VTextField
              v-model="item.skip_end_date"
              type="date"
              density="compact"
              hide-details
              variant="outlined"
            />
          </template>

          <!-- 작업 -->
          <template #item.actions="{ item }">
            <VBtn
              variant="outlined"
              color="primary"
              size="small"
              @click="saveDriverConfig(item)"
            >
              저장
            </VBtn>
          </template>
        </VDataTable>

        <p
          v-else
          class="text-center py-8"
        >
          고객사를 선택해주세요.
        </p>
      </VCardText>
    </VCard>

    <!-- 확인 다이얼로그 -->
    <VDialog
      v-model="confirmDialog"
      max-width="500"
    >
      <VCard>
        <VCardTitle>확인</VCardTitle>
        <VCardText>{{ confirmMessage }}</VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            variant="text"
            @click="confirmDialog = false"
          >
            취소
          </VBtn>
          <VBtn
            color="primary"
            @click="executeConfirm"
          >
            확인
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- 스낵바 -->
    <VSnackbar
      v-model="snackbar"
      :color="snackbarColor"
      :timeout="3000"
    >
      {{ snackbarMessage }}
    </VSnackbar>
  </div>
</template>

<style scoped>
.bg-success-lighten {
  background-color: rgba(var(--v-theme-success), 0.1) !important;
}
</style>
